import convert from "convert-units"

export type WeightUnit = "OUNCE" | "POUND" | "GRAM" | "KILOGRAM"
export type LengthUnit = "INCH" | "CENTIMETER" | "MILLIMETER"

const WEIGHT_UNIT_VALUES: WeightUnit[] = ["OUNCE", "POUND", "GRAM", "KILOGRAM"]
const LENGTH_UNIT_VALUES: LengthUnit[] = ["INCH", "CENTIMETER", "MILLIMETER"]

const WEIGHT_UNIT_VALUE_MAP = new Map<string, WeightUnit>(
  WEIGHT_UNIT_VALUES.map((value) => [value, value]),
)

const LENGTH_UNIT_VALUE_MAP = new Map<string, LengthUnit>(
  LENGTH_UNIT_VALUES.map((value) => [value, value]),
)

export const WEIGHT_UNITS_LONG: WeightUnit[] = [
  "OUNCE",
  "POUND",
  "GRAM",
  "KILOGRAM",
]

export const LENGTH_UNITS: LengthUnit[] = ["INCH", "MILLIMETER", "CENTIMETER"]

export const LENGTH_UNITS_SHORT: Record<LengthUnit, string> = {
  INCH: "in",
  MILLIMETER: "mm",
  CENTIMETER: "cm",
}

export const WEIGHT_UNITS_SHORT: Record<WeightUnit, string> = {
  OUNCE: "oz",
  POUND: "lb",
  GRAM: "g",
  KILOGRAM: "kg",
}

export function getWeightUnitShort(unitValue: string): string {
  const unit = WEIGHT_UNIT_VALUE_MAP.get(unitValue)
  if (unit) {
    return WEIGHT_UNITS_SHORT[unit]
  }
  throw Error(
    `Weight unit not found: ${unitValue} -> ${unit} -> ${WEIGHT_UNIT_VALUE_MAP}`,
  )
}

export function getLengthUnitShort(unitValue: string): string {
  const unit = LENGTH_UNIT_VALUE_MAP.get(unitValue)
  if (unit) {
    return LENGTH_UNITS_SHORT[unit]
  }
  throw Error(`Length unit not found: ${unitValue} -> ${unit}`)
}

export function convertUnit({
  value,
  unitShort,
  targetUnitShort,
}: {
  value: number
  unitShort: string
  targetUnitShort: string
}): number {
  return convert(value)
    .from(unitShort as convert.Unit)
    .to(targetUnitShort as convert.Unit)
}

export function convertWeightUnit(
  from: { value: number; unit: WeightUnit },
  toUnit: WeightUnit,
): number {
  return round(
    convertUnit({
      value: from.value,
      unitShort: getWeightUnitShort(from.unit),
      targetUnitShort: getWeightUnitShort(toUnit),
    }),
    3,
  )
}

export function convertLengthUnit(
  from: { value: number; unit: string },
  toUnit: string,
): number {
  return round(
    convertUnit({
      value: from.value,
      unitShort: getLengthUnitShort(from.unit),
      targetUnitShort: getLengthUnitShort(toUnit),
    }),
    2,
  )
}

export function convertLengthUnitToCentimeters(
  value: number,
  unit: string,
): number {
  return round(
    convertUnit({
      value,
      unitShort: getLengthUnitShort(unit),
      targetUnitShort: LENGTH_UNITS_SHORT.CENTIMETER,
    }),
    2,
  )
}

export function formatLengthInches(value: number, unit: string): string {
  return `${convertLengthUnit({ value, unit }, "INCH").toString()}″`
}

// TODO: Not directly related to units, move to a different location
export function round(value: number, precision = 2): number {
  const denominator = Math.pow(10, precision)
  return Math.round(value * denominator) / denominator
}
